import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import Layout from "../utils/Layout";
import BiotechIcon from "@mui/icons-material/Biotech";
import ScienceIcon from "@mui/icons-material/Science";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Inventory } from '@mui/icons-material';
import SourceIcon from '@mui/icons-material/Source';
import { GiGrainBundle } from "react-icons/gi";
import Footer from "../utils/Footer";
import { useNavigate } from "react-router-dom";
import { appColours } from "../../helpers/colorVars";
import { useTranslation } from "react-i18next";
import { useTitle } from "../utils/useTitle";
import { useAnalyticsEventTracker } from "../utils/useAnalyticsEventTracker";


/**
 * Home Page Component
 * This displays the entire home page
 * It displays: 
 * - the banner where you can get started
 * - the platform services available
 * - the description of the page
 * - what the website offers
 * 
 * @returns {JSX.Element} Home Page Component
 */
export const HomePage = () => {
  useTitle("Home");
  const navigate = useNavigate();
  const token = sessionStorage.getItem("authToken");

  const eventTracker = useAnalyticsEventTracker();
  const { t } = useTranslation();

  return (
    <>
      <Banner t={t} eventTracker={eventTracker} navigate={navigate} token={token} />
      
      <Layout>
        <PlatformService t={t} />
        <Description t={t}/>
        <Offers t={t} />
        <GetStartedButton t={t} navigate={navigate} token={token} eventTracker={eventTracker} marginTop={5}/>

        {/* <AlertBar
          open={Object.values(errors).some((error) => error)}
          message={errorMessage}
          severity={"error"}
          onClose={() => setErrors({})}
          /> */}
      </Layout>

      <Footer />
    </>
  );
};


/**
 * Banner Component
 * This component displays the banner section of the home page
 * 
 * @param {Function} t
 * @param {Function} eventTracker
 * @param {Function} navigate
 * @param {String} token
 * @returns {JSX.Element} Banner Component
 */
function Banner({ t, eventTracker, navigate, token }) {
  return (
    <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100vh",
          backgroundImage: `url('assets/images/banner1.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "black",
          textAlign: "center",
        }}
      >
        <div>
          <Typography variant="h1" sx={{ fontSize: "70px", fontWeight: "800" }}>
            {t("banner.header")}
          </Typography>
          <hr style={{ border: "3px solid black" }} />
          <Typography variant="h5" fontWeight={800} marginTop={"10px"}>
            {t("banner.subHeader")}
          </Typography>
          <Typography
            variant="h6"
            fontWeight={800}
            marginTop={"10px"}
            color={"darkgreen"}
          >
            {t("banner.description")}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {t("banner.caption")}
          </Typography>

          <GetStartedButton t={t} navigate={navigate} token={token} eventTracker={eventTracker} marginTop={20}/>
        </div>
      </div>
  );
}

/**
 * Platform Service Component
 * this component displays the services available on the platform
 * 
 * @param {Function} t 
 * @returns {JSX.Element} Platform Service Component
 */
function PlatformService({ t }) {
  return (
    <div id="platform-info">
      <Grid container sx={{ marginTop: "70px", marginBottom: "120px" }}>
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight={"600"} textAlign="center">
            {t("platformSection.header")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight={"500"}
            marginTop={"30px"}
            textAlign={"center"}
          >
            {t("platformSection.description")}
          </Typography>

          <Typography
            variant="h6"
            color={"green"}
            fontWeight={"600"}
            marginTop={"30px"}
            textAlign={"center"}
          >
            {t("platformSection.caption")}
          </Typography>
        </Grid>
        
        <Grid container xs={12} style={{ marginTop: "30px" }} spacing={3}>
          <PlatformServiceCard t={t} color={"green"} index={1} />
          <PlatformServiceCard t={t} color={"black"} index={2} />
          <PlatformServiceCard t={t} color={"green"} index={3} />
        </Grid>

        <Grid item xs={12}>
          <Typography
              variant="h6"
              color={"green"}
              fontWeight={"600"}
              marginTop={"40px"}
              textAlign={"center"}
          >
            {t("platformSection.account")}
          </Typography>
        </Grid>

        <Grid container xs={12} style={{ marginTop: "10px" }} spacing={3}>
          <PlatformServiceCard t={t} color={"black"} index={4} />
          <PlatformServiceCard t={t} color={"green"} index={5} />
          <PlatformServiceCard t={t} color={"black"} index={6} />
        </Grid>
        
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight={"500"}
            marginTop={"40px"}
            textAlign={"center"}
          >
            {t("platformSection.footer")}
          </Typography>
        </Grid>
      </Grid>      
    </div>
  );
}

/**
 * Platform Service Card Component
 * this component provides the template for displaying the different platform service cards
 * 
 * @param {Function} t
 * @param {String} color
 * @param {Number} index
 * @returns {JSX.Element} Platform Service Card Component
 */
function PlatformServiceCard({ t, color, index }) {
  return (
    <Grid item xs={4}>
      <Card
        variant="outlined"
        sx={{ padding: "15px", borderColor: color }}
      >
        <CardHeader
          sx={{ gap: '10px' }}
          avatar={
            <Avatar
              sx={{
                bgcolor: color,
                width: "80px",
                height: "80px",
              }}
              aria-label="recipe"
              sizes="large"
            >
              { index === 1 && <GiGrainBundle style={{ fontSize: "50px" }} /> }
              { index === 2 && <BiotechIcon style={{ fontSize: "50px" }} /> }
              { index === 3 && <ScienceIcon style={{ fontSize: "50px" }} /> }
              { index === 4 && <Inventory style={{ fontSize: "50px" }} /> }
              { index === 5 && <FilterAltIcon style={{ fontSize: "50px" }}/> }
              { index === 6 && <SourceIcon style={{ fontSize: "50px" }}/> }
            </Avatar>
          }
          title={
            <Typography variant="p" fontWeight={"500"}>
              {t(`platformSection.item${index}`)}
            </Typography>
          }
        />
      </Card>
    </Grid>
  );
}

/**
 * Description Component
 * this provides a description of what the website does
 * 
 * @param {Function} t
 * @returns Description Component
 */
function Description({ t }) {
  return (
    <div id="genie">
      <Grid container sx={{ marginTop: "120px", marginBottom: "120px" }}>
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight={"600"} textAlign="center">
            {t("formulationSection.header")}
          </Typography>
        </Grid>

        <Grid container sx={{ marginTop: "50px" }}>
          <Grid item xs={6}>
            <img
              src="assets/images/banner1.jpg"
              alt="Banner2"
              style={{ width: "80%", height: "100%", objectFit: "cover" }}
            />
          </Grid>

          <Grid item xs={6} sx={{ marginTop: "100px" }}>
            <DescriptionSection t={t} index={1}/>
            <DescriptionSection t={t} index={2}/>
            <DescriptionSection t={t} index={3}/>
          </Grid>
        </Grid>

      </Grid>
    </div>
  );
}

/**
 * Description Section Component
 * This provides a template for displaying the description
 * of the website
 * 
 * @param {Function} t
 * @param {Number} index  
 * @returns {JSX.Element} DescriptionSection 
 */
function DescriptionSection({ t, index }) {
  return (
    <Stack
      direction="row"
      spacing={4}
      alignItems="flex-start"
      sx={{ marginBottom: "50px" }}
    >
      <Typography
        variant="h5"
        sx={{
          marginTop: "45px",
          fontSize: "80px",
          fontWeight: "700",
          color: "green",
        }}
      >
        {`0${index}`}
      </Typography>
      <Typography variant="h6" sx={{ paddingTop: "15px" }}>
        {t(`formulationSection.item${index}`)}
      </Typography>
    </Stack>
  );
}

/**
 * Offers Component
 * this component displays what the page offers to the users
 * 
 * @param {Function} t 
 * @returns {JSX.Element} Offers Component 
 */
function Offers({ t }) {
  return (
    <Grid container sx={{ marginTop: "120px", marginBottom: "120px" }}>
      <Grid item xs={12}>
        <Typography variant="h4" fontWeight={"600"} textAlign="center">
          {t("serviceSection.header")}
        </Typography>
      </Grid>

      <Grid
        container
        columns={15}
        style={{ marginTop: "30px" }}
        spacing={3}
      >
        <OfferCard t={t} index={1} />
        <OfferCard t={t} index={2} />
        <OfferCard t={t} index={3} />
        <OfferCard t={t} index={4} />
        <OfferCard t={t} index={5} />
      </Grid>
    </Grid>
  );
}

/**
 * Offer Card Component
 * This component provides a template for the offer boxes on the page
 * 
 * @param {Function} t
 * @param {Number} index 
 * @returns Offer Card Component
 */
function OfferCard({ t, index }) {
  return (
    <Grid item xs={3}>
      <Card
        variant="outlined"
        sx={{
          padding: "15px",
          borderColor: "green",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          maxHeight: "200px"
        }}
      >
        <CardHeader
          sx={{
            "& .MuiCardHeader-avatar": {
              marginRight: 0
            }
          }}
          avatar={
            <Avatar
              sx={{
                bgcolor: "white",
                width: "80px",
                height: "80px",
                border: `2px solid ${appColours.lightGreen}`
              }}
              aria-label="recipe"
              sizes="large"
            >
              {/* <GiGrain style={{ fontSize: "50px" }} /> */}
              <img
                src={`/assets/icons/icon${index}.png`}
                alt={`icon-${index}`}
                width={"100px"}
                height={"100px"}
                // style={{ padding: "10px" }}
              />
            </Avatar>
          }
        />
        <CardContent>
          <Typography
            variant="h6"
            fontWeight={"700"}
            sx={{ textAlign: "center", fontSize: "15px" }}
          >
            {t(`serviceSection.item${index}`)}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

/**
 * Get Started Button Component
 * This component displays the get started buttons
 * 
 * @param {Function} t
 * @param {Function} navigate
 * @param {String} token
 * @param {Function} eventTracker
 * @param {String} marginTop
 * @returns {JSX.Element} Get Started Button Component
 */
function GetStartedButton({ t, navigate, token, eventTracker, marginTop }) {
  return (
    <Grid
      container
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "20vh",
      }}
    >
      {!token && (
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="success"
            size="large"
            style={{ height: "55px", marginTop: marginTop }}
            onClick={() => {
              eventTracker("get_started")
              token ? navigate("/dashboard") : navigate("/signup");
            }}
          >
            {token ? "Go to Dashboard" : t("banner.btnText")}
          </Button>
        </Grid>
      )}
    </Grid>
  );
}